import Cookies from "js-cookie";
import i18n from "@shared/i18n/i18n";
import useNavigateToEntity from "../navigation/useNavigateToEntity";
import { findClinicByPath, findEventOrganizerByPath } from "@shared/lib";
import { ROOTS, ROUTES, USER_ROUTES } from "@app/config/constants";
import { setIsInitializingApp } from "@app/store/appSlice";
import { setUserPermissions } from "@features/user/model/userSlice";
import { useAfterLoginTask } from "../user/useAfterLoginTask";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissionAccessList } from "@shared/hooks/user/usePermission";
import { useUser } from "@shared/hooks/user/useUser";
import dayjs from "dayjs";
import { getLocalStorage, removeLocalStorage } from "@shared/lib";
import { useSudoUser } from "../user/useSudoUser";
import { useGetMyCompanies } from "./useGetMyCompanies";
import { UserPermissionAccess, HealthcareCompanyType } from "@shared/types/commonTypes";
/* eslint-disable react-hooks/exhaustive-deps */

export const AppType = {
  hasDentalClinic: "dental_clinic",
  hasEventCompany: "event_organizer",
  hasDentalLab: "dental_lab",
} as const;

export const useInitializeApp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { navigateToEntity: navigateToClinic } = useNavigateToEntity({
    rootPath: ROOTS.CLINIC,
    lastVisitedKey: "__LAST_VISITED_C_ID",
    findEntityByPath: findClinicByPath,
  });

  const { navigateToEntity: navigateToEventOrganizer } = useNavigateToEntity({
    rootPath: ROOTS.EVENTS,
    lastVisitedKey: "__LAST_VISITED_E_ID",
    findEntityByPath: findEventOrganizerByPath,
  });

  // why /v/ott check? reason: in case of sudo with existing session don't call the APIs and enter into app initialization until we get new token from ott
  const hasToken =
    !!Cookies.get("access_token") && !location.pathname.includes("/v/ott");
  const hasInviteToken = !!getLocalStorage("invite_token");

  const {
    data: sudoUser,
    isLoading: isSudoLoading,
    isSuccess: isSudoUserLoaded,
  } = useSudoUser(hasToken);
  const {
    user,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useUser(hasToken);

  // ignore afterLoginTask call if we have inviteToken in localstorage
  const afterLoginTask = useAfterLoginTask(
    hasToken && isSudoUserLoaded,
    !!sudoUser?.sudo
  );
  const {
    data: tdoTask,
    isFetching: isTDOLoading,
    isSuccess: isTDOLoaded,
  } = afterLoginTask;

  const {
    permissionAccessList,
    isFetching: isPermissionsLoading,
    isError: isPermissionError,
  } = usePermissionAccessList(
    hasToken && !tdoTask?.next && !isTDOLoading && isTDOLoaded
  );

  // changing isFetching to IsLoading so that we don;t get loading window on update company
  const { companies, isLoading: isCompaniesLoading } = useGetMyCompanies({
    type: HealthcareCompanyType.DENTAL_CLINIC,
    enabled: Boolean(
      hasToken &&
        !tdoTask?.next &&
        !isTDOLoading &&
        isTDOLoaded &&
        !isPermissionsLoading &&
        permissionAccessList &&
        permissionAccessList?.includes(UserPermissionAccess.CLINIC)
    ),
  });
  const { companies: eventOrganizers, isLoading: isEventOrganizersLoading } =
    useGetMyCompanies({
      type: HealthcareCompanyType.EVENT_ORGANIZER,
      enabled: Boolean(
        hasToken &&
          !tdoTask?.next &&
          !isTDOLoading &&
          isTDOLoaded &&
          !isPermissionsLoading &&
          permissionAccessList &&
          permissionAccessList?.includes(UserPermissionAccess.EVENT)
      ),
    });

  const isLoading =
    isPermissionsLoading ||
    isUserLoading ||
    isCompaniesLoading ||
    isEventOrganizersLoading ||
    isTDOLoading ||
    isSudoLoading;

  useEffect(() => {
    dispatch(setIsInitializingApp(isLoading));
  }, [dispatch, isLoading]);

  useEffect(() => {
    if (isPermissionError || isUserError) {
      dispatch(setIsInitializingApp(false));
    }
  }, [dispatch, isPermissionError, isUserError, navigate]);

  useEffect(() => {
    if (user && !isUserLoading) {
      const savedLanguage = localStorage.getItem("selectedLanguage");

      if (!savedLanguage) {
        localStorage.setItem("selectedLanguage", user.language.iso);
        i18n.changeLanguage(user.language.iso);
        dayjs.locale(user.language.iso);
      }
    }
  }, [user, isUserLoading]);

  useEffect(() => {
    if (!isTDOLoading && isTDOLoaded) {
      if (tdoTask?.next === "questionnaire") {
        navigate(ROOTS.USER + "/" + USER_ROUTES.QUESTIONNAIRE);
      } else if (tdoTask?.next === "invites") {
        navigate(ROOTS.USER + "/" + USER_ROUTES.INVITES);
      } else if (tdoTask?.next === "autoJoin") {
        navigate(ROOTS.USER + "/" + USER_ROUTES.AUTOJOIN);
      }
    }
  }, [isTDOLoading, isTDOLoaded]);

  useEffect(() => {
    if (permissionAccessList && !isPermissionsLoading && !hasInviteToken) {
      dispatch(setUserPermissions(permissionAccessList));

      // Don't auto-navigate if user is on specific pages
      // These routes should not trigger automatic navigation during app initialization
      const protectedRoutes = [
        { path: `${ROOTS.CLINIC}/add`, type: 'exact' },
        { path: `${ROOTS.EVENTS}/add`, type: 'exact' },
        { path: ROOTS.USER, type: 'startsWith' },
        { path: '/settings', type: 'includes' },
      ];
      
      const currentPath = location.pathname;
      const isProtectedRoute = protectedRoutes.some(route => {
        switch (route.type) {
          case 'exact':
            return currentPath === route.path;
          case 'startsWith':
            return currentPath.startsWith(route.path);
          case 'includes':
            return currentPath.includes(route.path);
          default:
            return false;
        }
      });
      
      // If on protected route, just finish initialization without navigation
      if (isProtectedRoute) {
        dispatch(setIsInitializingApp(false));
        return;
      }

      const redirectPathBeforeLogin = getLocalStorage(
        "__REDIRECT_PATH"
      ) as string;
      if (redirectPathBeforeLogin?.includes(ROUTES.QES_CONNECT)) {
        removeLocalStorage("__REDIRECT_PATH");
        navigate(redirectPathBeforeLogin);
      } else if (
        permissionAccessList.length === 1 &&
        permissionAccessList.includes(UserPermissionAccess.CLINIC) &&
        !isCompaniesLoading
      ) {
        // clinic mode
        // get clinics and goto 1st clinic if its 1 else show clinics list
        navigateToClinic(companies);
      } else if (
        permissionAccessList.length === 1 &&
        permissionAccessList.includes(UserPermissionAccess.EVENT) &&
        !isEventOrganizersLoading
      ) {
        // events mode - navigate to events page
        navigateToEventOrganizer(eventOrganizers);
      } else if (
        permissionAccessList.length === 1 &&
        (permissionAccessList.includes(UserPermissionAccess.RECORDS) ||
          permissionAccessList.includes(UserPermissionAccess.INVOICES))
      ) {
        dispatch(setIsInitializingApp(false));
        navigate(ROOTS.PATIENT);
      } else if (permissionAccessList.length > 1) {
        if (
          getLocalStorage("LastSelectedAppMode") === ROOTS.CLINIC &&
          !isCompaniesLoading
        ) {
          dispatch(setIsInitializingApp(false));
          navigateToClinic(companies);
        } else if (
          getLocalStorage("LastSelectedAppMode") === ROOTS.EVENTS &&
          !isEventOrganizersLoading
        ) {
          dispatch(setIsInitializingApp(false));
          navigateToEventOrganizer(eventOrganizers);
        } else if (!isCompaniesLoading && !isEventOrganizersLoading) {
          dispatch(setIsInitializingApp(false));
          navigate("/");
        }
      }
    }
  }, [
    permissionAccessList,
    isPermissionsLoading,
    isCompaniesLoading,
    isEventOrganizersLoading,
  ]);
};
